import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import {
  DataTableComponent,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';

import {
  PaymentHistory,
  PaymentHistorySearchParams,
  PAYMENT_HISTORY_TABLE_COLUMNS,
  PAYMENT_HISTORY_SEARCH_CONFIG,
  formatCurrency,
  getPaymentStatusDisplayText,
  getPaymentMethodDisplayText,
  getPaymentPlatformDisplayText,
  getPaymentStatusSeverity,
  getPaymentStatusIcon,
  getPaymentStatusStyle,
} from './payment-history.config';
import { DateFormatterService } from '@shared/services/date-formatter.service';
import { PaymentHistoryService } from './payment-history.service';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';

@Component({
  selector: 'app-payment-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    DialogModule,
    SelectModule,
    ToastModule,
    TranslateModule,
    DataTableComponent,
    SearchFormComponent,
    AppButtonComponent,
  ],
  templateUrl: './payment-history.component.html',
  styleUrl: './payment-history.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class PaymentHistoryComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Signals
  paymentHistory = signal<PaymentHistory[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  selectedPayment = signal<PaymentHistory | null>(null);
  currentSearchParams = signal<PaymentHistorySearchParams | undefined>(
    undefined,
  );

  // Computed values
  tableColumns = computed(() => PAYMENT_HISTORY_TABLE_COLUMNS);
  searchConfig = computed(() => PAYMENT_HISTORY_SEARCH_CONFIG);

  // Pagination
  currentPage = 1;
  pageSize = 20;

  constructor(
    private readonly paymentHistoryService: PaymentHistoryService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly messageService: MessageService,
    private readonly dateFormatterService: DateFormatterService,
  ) {}

  ngOnInit(): void {
    this.loadPaymentHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPaymentHistory(): void {
    this.loading.set(true);
    const searchParams = this.currentSearchParams();
    const params: PaymentHistorySearchParams = {
      ...searchParams,
      pageRequest: {
        page: this.currentPage - 1, // API uses 0-based pagination
        size: this.pageSize,
        sortBy: 'creationDate',
        ascending: false,
      },
    };

    this.paymentHistoryService
      .getPaymentHistory(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.paymentHistory.set(response.data);
          this.totalRecords.set(response.total);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading payment history:', error);
          this.loading.set(false);
        },
      });
  }

  onSearch(searchData: SearchFormData): void {
    this.loading.set(true);
    // Convert search form data to PaymentHistorySearchParams
    const searchParams: PaymentHistorySearchParams = {
      orderCode: searchData['orderCode'] || undefined,
      status: searchData['status'] || undefined,
      pageRequest: {
        page: 0, // Will be set in loadPaymentHistory
        size: this.pageSize,
        sortBy: 'creationDate',
        ascending: false,
      },
    };

    this.currentPage = 1; // Reset to first page when searching
    this.currentSearchParams.set(searchParams);
    this.loadPaymentHistory();
  }

  onReset(): void {
    this.currentPage = 1; // Reset to first page
    this.currentSearchParams.set(undefined);
    this.loadPaymentHistory();
  }

  onPageChange(event: PaginationEvent): void {
    this.currentPage = (event.page || 0) + 1; // Convert from 0-based to 1-based
    this.pageSize = event.rows || 20;
    this.loadPaymentHistory();
  }

  onViewDetails(payment: PaymentHistory): void {
    if (payment?.id) {
      this.router.navigate(['/finance/payment-history/view', payment.id]);
    }
  }

  // Helper methods for template
  getPaymentStatusIcon(status: string): string {
    return getPaymentStatusIcon(status);
  }

  getPaymentStatusDisplayText(status: string): string {
    return getPaymentStatusDisplayText(status);
  }

  getPaymentMethodDisplayText(method: string): string {
    return getPaymentMethodDisplayText(method);
  }

  getPaymentPlatformDisplayText(platform: string): string {
    return getPaymentPlatformDisplayText(platform);
  }

  getPaymentStatusSeverity(
    status: string,
  ): 'success' | 'secondary' | 'warning' | 'danger' | 'info' {
    return getPaymentStatusSeverity(status);
  }

  getPaymentStatusStyle(status: string): any {
    return getPaymentStatusStyle(status);
  }

  formatCurrency(amount: number, currency: string = 'VND'): string {
    return formatCurrency(amount, currency);
  }

  formatDate(dateString: string): string {
    return this.dateFormatterService.formatDateTime(dateString);
  }
}
